import { Stack, Typography, Link as MuiLink, useMediaQuery, useTheme } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { palette } from '../../theme';
import type { TeamMember } from './TeamMemberDrawer.types';

interface TeamMemberDrawerHeroProps {
  member: TeamMember;
}

export default function TeamMemberDrawerHero({ member }: TeamMemberDrawerHeroProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('laptop'));

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      alignItems='center'
      justifyContent='center'
      gap={isMobile ? 3 : 6}
      sx={{
        background: 'linear-gradient(135deg, #5C3426 0%, #2C2C2C 100%)',
        px: { mobile: 3, laptop: 8 },
        py: { mobile: 5, laptop: 7 },
        minHeight: isMobile ? 'auto' : '45dvh',
      }}
    >
      {/* Photo */}
      <Stack
        sx={{
          width: isMobile ? 200 : 280,
          height: isMobile ? 200 : 280,
          borderRadius: '50%',
          overflow: 'hidden',
          flexShrink: 0,
          border: `2px solid ${palette.border}`,
        }}
      >
        <img
          src={member.photo}
          alt={member.name}
          loading='lazy'
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'top',
          }}
        />
      </Stack>

      {/* Name, title, email */}
      <Stack gap={1.5} alignItems={isMobile ? 'center' : 'flex-start'}>
        <Typography
          variant='h3'
          sx={{
            color: palette.white,
            fontWeight: 700,
            textAlign: isMobile ? 'center' : 'start',
          }}
        >
          {member.name}
        </Typography>
        <Typography variant='body1' sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {member.title}
        </Typography>
        <Stack direction='row' gap={1} alignItems='center'>
          <EmailIcon fontSize='small' sx={{ color: palette.accent }} />
          <MuiLink
            href={`mailto:${member.email}`}
            sx={{
              color: palette.white,
              textDecoration: 'none',
              '&:hover': { color: palette.accent },
            }}
          >
            <Typography variant='body2' dir='ltr'>
              {member.email}
            </Typography>
          </MuiLink>
        </Stack>
      </Stack>
    </Stack>
  );
}
